// @flow
import * as React from 'react';
import type {MapStateToProps} from 'react-redux';
import {bindActionCreators} from "redux";
import {connect} from 'react-redux';
import {setModal} from '../../ui/ui.actions.js';
import { isUserEventInstructor } from './utils/permissions';


import {
  AIR_EVENT_TYPE,
  DISCREPANCY_ADD,
  DISCREPANCY_VIEW,
  EVENT_ADD,
  EVENT_CANCEL,
  EVENT_CHECKIN,
  EVENT_CHECKOUT,
  EVENT_EDIT,
  EVENT_RESOURCE,
  EVENT_VIEW,
  INSTRUCTORS_SCHEDULE,
  MAINTENANCE_EVENT_TYPE
} from '../calendar.consts.js';

import { selectIsNoChangesInEvents } from '../calendar.selectors.js';
import {
  ADD_PASSENGER,
  EDIT_PASSENGER,
  LOAD_MANIFEST,
  WEIGHT_AND_BALANCE
} from '../weight-and-balance/weight-and-balance.consts';

import {getWeightAndBalance} from "../weight-and-balance/weight-and-balance.actions";

type OwnProps = {|
  balanceStatus: boolean,
|};


type MappedState = {|
  URL: URL,
  eventForm: eventForm,
|};

type Props = {|
  ...OwnProps,
  ...MappedState
|};

function SubmitButton(props: { fullText: string, shortText: string }) {
  return (
    <span>
      <span className="hidden-xs">{props.fullText}</span>
      <span className="hidden-sm hidden-md hidden-lg">{props.shortText}</span>
    </span>
  );
}

class EventModalFooter extends React.Component<Props> {
  constructor(props: Props) {
    super(props);
    this.getNotFilledCustomFieldsLength = this.getNotFilledCustomFieldsLength.bind(this);
  }

  componentDidMount() {
    const { getWeightAndBalance } = this.props;
    getWeightAndBalance();
  }

  getNotFilledCustomFieldsLength(formFields) {
    if (typeof formFields === 'object') {
      const emptyCustomFields = Object.keys(formFields).filter(
        fieldID => formFields[fieldID] === ''
      );

      const notFilledCustomFields = emptyCustomFields.filter(
        fieldID => this.props.customFields[fieldID].required === '1'
      );

      return notFilledCustomFields.length;
    }
    return 0;
  }

  render() {
    const {
      UI,
      URL,
      user,
      eventForm,
      discrepancyForm,
      checkinForm,
      event,
      eventModalData,
      accessgroups,
      organizationsAccess,
      balanceStatus,
      organization,
      resources
    } = this.props;

    const noDefaultPilot = organization.organizationSettings.pilotOptional === "1";

    if (!URL.eventParams.action) return null;

    const orgAccess = organizationsAccess[event.organizationUnique]
      ? organizationsAccess[event.organizationUnique].accessGroups
      : {};

    const isAirEvent = eventForm.type === AIR_EVENT_TYPE;
    const isMaintenanceEvent = eventForm.type === MAINTENANCE_EVENT_TYPE;
    const noResource = eventForm.resources.length === 0;

    const isPilot = event.pilotID === user.id;
    const isValidEvent = event && event.organizationUnique;

    const isUserInstructor = isUserEventInstructor(eventForm, user, resources);

    const canManageAir = orgAccess && orgAccess.calendarManageAnothers && !isPilot && isAirEvent;
    const pilotCanManageHisAir = orgAccess && orgAccess.calendarManageMy && isPilot && isAirEvent;
    const gotMaintenancePermissions = orgAccess && orgAccess.maintenanceManage && isMaintenanceEvent;


    const availableToCheckoutOrCheckIn = isValidEvent && (canManageAir || pilotCanManageHisAir || gotMaintenancePermissions);
    const regularManage = isValidEvent && (canManageAir || pilotCanManageHisAir);
    const maintenanceManage = isValidEvent && gotMaintenancePermissions;

    const availableToManage = regularManage || maintenanceManage || isUserInstructor;

    const isFinancialModule =
      organizationsAccess[event.organizationUnique] &&
      organizationsAccess[event.organizationUnique].financialModule === '1';

    const eventWithNoPilotEnabled = accessgroups.calendarManageAnothers && !eventForm.pilotID && noDefaultPilot;

    const eventWithoutPilot = (!eventForm.pilotID && isAirEvent);

    const enforceComments = parseInt(eventModalData.enforseComments, 10) === 1
    const noComments = !eventForm.comments || (eventForm.comments && eventForm.comments.length === 0);
    const noResourceAndInstructor = (noResource && !eventForm.instructorID);
    const noResourceMaintenance = (noResource && isMaintenanceEvent);
    const isEmptyFieldsPresent = this.getNotFilledCustomFieldsLength(eventForm.customFields) && isAirEvent;
    const enableTrackFlight = organization.organizationSettings.enableTrackFlight === "1";
    const forceEnableManifest = !(localStorage.getItem('showManifest') === 'true');

    switch (URL.eventParams.action) {
      case EVENT_ADD:
        const disabled = UI.progress ||
          (
            eventWithoutPilot && !eventWithNoPilotEnabled && !isUserInstructor
            || (enforceComments && noComments)
            || noResourceAndInstructor
            || noResourceMaintenance
            || isEmptyFieldsPresent
            || !eventModalData.success
          );

        return (
          <label
            htmlFor={EVENT_ADD}
            className="btn btn-sm btn-success pull-right add-event-label btn-full-width"
            disabled={disabled}
          >
            <span className="icon plus-circled" />{' '}
            {eventForm.type === AIR_EVENT_TYPE && eventForm.addToWaiting === '0' ? (
              <SubmitButton fullText="Add Event" shortText="Event" />
            ) : eventForm.type === AIR_EVENT_TYPE && eventForm.addToWaiting === '1' ? (
              <SubmitButton fullText="Add to Waiting List" shortText="Waiting List" />
            ) : eventForm.type === MAINTENANCE_EVENT_TYPE ? (
              <SubmitButton fullText="Add Resource Maintenance" shortText="Maintenance" />
            ) : null}
          </label>
        );

      case EVENT_EDIT:
        if (!availableToManage) return null;

        let isDisabled =
          UI.progress ||
          (
            eventWithoutPilot && !eventWithNoPilotEnabled && !isUserInstructor
            || (enforceComments && noComments)
            || noResourceAndInstructor
            || noResourceMaintenance
            || isEmptyFieldsPresent
            || !eventModalData.success
            || this.props.isNoChangesInEvents
          );

        return (
          <label
            htmlFor={EVENT_EDIT}
            className="btn btn-sm btn-success pull-right add-event-label btn-full-width btn-full-width-no-margin"
            disabled={isDisabled}
          >
            <span className="icon plus-circled" /> Save{' '}
            {eventForm.type === MAINTENANCE_EVENT_TYPE ? 'Maintenance' : 'Event'}
          </label>
        );

      case EVENT_VIEW:
        const eventCompleted =
          parseInt(event.checkOutEnable, 10) === 0 &&
          parseInt(event.checkInEnable, 10) === 0 &&
          parseInt(event.aircraftExists, 10) === 1;

        const isMaintenanceEvent = event.type === MAINTENANCE_EVENT_TYPE;

        const showDeleteButton = (
            (orgAccess.calendarEventDelete && (event.type === AIR_EVENT_TYPE || isMaintenanceEvent)) || (isMaintenanceEvent && maintenanceManage))
            && event.recurring !== '1' && !eventCompleted;

        return (
          <div className="event-footer-btn-groups">
            <div className="event-footer-btn-group">
              { showDeleteButton ? (
                <label
                  disabled={UI.progress}
                  htmlFor={`${EVENT_VIEW}-delete`}
                  className="btn btn-sm btn-danger event-footer-btn"
                  title="Delete Event"
                >
                  <span className="icon trash" /> Delete
                </label>
              ) : null}
              {orgAccess.calendarEventDelete &&
              (event.type === AIR_EVENT_TYPE ||
                (event.type === MAINTENANCE_EVENT_TYPE && maintenanceManage)) &&
              event.recurring === '1' &&
              !eventCompleted ? (
                <div className="delete-dropdown">
                  <a
                    tabIndex="0"
                    role="button"
                    className="btn btn-sm btn-danger delete-dropdown-trigger event-footer-btn"
                  >
                    <span className="icon trash" /> Delete Event <span className="caret" />
                  </a>
                  <ul className="delete-dropdown-view">
                    <li className="delete-dropdown-view-item">
                      <label htmlFor={`${EVENT_VIEW}-delete-current`} disabled={UI.progress}>
                        Current
                      </label>
                    </li>
                    <li className="delete-dropdown-view-item">
                      <label htmlFor={`${EVENT_VIEW}-delete-not-started`} disabled={UI.progress}>
                        Not started
                      </label>
                    </li>
                    <li className="delete-dropdown-view-item">
                      <label htmlFor={`${EVENT_VIEW}-delete-following`} disabled={UI.progress}>
                        Following
                      </label>
                    </li>
                    <li className="delete-dropdown-view-item">
                      <label htmlFor={`${EVENT_VIEW}-delete-all`} disabled={UI.progress}>
                        All
                      </label>
                    </li>
                  </ul>
                </div>
              ) : null}


              { accessgroups.checkOutAircraft ? (
                availableToManage &&
                parseInt(event.checkOutEnable, 10) == 0 &&
                parseInt(event.checkInEnable, 10) == 1 &&
                parseInt(event.aircraftExists, 10) === 1 ? (
                  <label
                    htmlFor={`${EVENT_VIEW}-cancel-checkout`}
                    className="btn btn-sm btn-warning event-footer-btn"
                    title="Cancel Event"
                    disabled={UI.progress}
                  >
                    <span className="icon block" /> Cancel Check Out
                  </label>
                ) : eventCompleted ? (
                  <label
                    htmlFor={`${EVENT_VIEW}-cancel-checkin`}
                    className="btn btn-sm btn-warning event-footer-btn"
                    title="Cancel Event"
                    disabled={UI.progress}
                  >
                    <span className="icon block" /> Cancel Check In
                  </label>
                ) : null
              ) : null}
              {availableToManage && event.type === AIR_EVENT_TYPE && !eventCompleted ? (
                <label
                  htmlFor={`${EVENT_VIEW}-cancel`}
                  className="btn btn-sm btn-warning event-footer-btn"
                  title="Cancel Event"
                  disabled={UI.progress}
                >
                  <span className="icon block" /> Cancel
                </label>
              ) : null}
            </div>
            <div className="event-footer-btn-group">
              {isFinancialModule && (orgAccess.productsView || isPilot) ? (
                event.invoiceId ? (
                  <a
                    className="btn btn-sm btn-default event-footer-btn"
                    title="View Invoice"
                    target="_blank"
                    href={
                      orgAccess.productsView
                        ? `${window.serverData._uri}/org/${event.organizationUnique}/invoices/${event.invoiceId}/`
                        : `${window.serverData._uri}/invoices/${event.invoiceId}/`
                    }
                  >
                    {`View ${event.invoicePaidStatus[0].toUpperCase() +
                    event.invoicePaidStatus.slice(1)} Invoice #${event.invoiceNumber || ''}`}
                  </a>
                ) : orgAccess.productsView && event.type === 'air' ? (
                  <label
                    htmlFor={`${EVENT_VIEW}-create-invoice`}
                    className="btn btn-sm btn-default event-footer-btn"
                    title="Create Invoice"
                  >
                    Create Invoice
                  </label>
                ) : null
              ) : null}
              {event.weightAndBalanceEnable ? (
                <div className="tooltip-container">
                  {balanceStatus ?
                    (<span className="tooltip">Available only when W&B is within limits</span>) : null}
                  <label
                    htmlFor={LOAD_MANIFEST}
                    className="btn btn-default event-footer-btn"
                    disabled={UI.progress || (balanceStatus && forceEnableManifest)}
                  >
                    <span className="icon newspaper" /> Print Load Manifest
                  </label>
                </div>
              ) : null}
              {availableToManage &&
              event.type === AIR_EVENT_TYPE &&
              parseInt(event.aircraftExists, 10) === 1 &&
              !eventCompleted ? (
                <label
                  htmlFor={`${EVENT_VIEW}-waiting`}
                  className="btn btn-sm btn-info event-footer-btn"
                  title="Add to Waiting List"
                  disabled={UI.progress}
                >
                  <span className="icon calendar-add" /> Add to Waiting List
                </label>
              ) : null}
              {availableToManage && parseInt(event.availableForEdit, 10) === 1 ? (
                <label
                  htmlFor={`${EVENT_VIEW}-edit`}
                  className="btn btn-sm btn-info event-footer-btn"
                  title="Edit Event"
                  disabled={UI.progress}
                >
                  <span className="icon pencil" /> Edit
                </label>
              ) : null}
            </div>
          </div>
        );

      case EVENT_CANCEL:
        const disableCancel =
          UI.progress ||
          !event.reason || (event.reason && event.reason === '') ||
          ((event.reason === 'other' && !event.textReason) ||
            (event.reason === 'other' && event.textReason.length < 10)) ||
          (event.eventsForAction === '' && event.recurring === '1');

        return (
          <label
            htmlFor={EVENT_CANCEL}
            className="btn btn-sm btn-warning pull-right event-footer-btn"
            disabled={ disableCancel }
          >
            <span className="icon block" /> Cancel Event
          </label>
        );

      case EVENT_CHECKOUT:
        if (!availableToCheckoutOrCheckIn) return null;

        return (
          <div className="checkout-btn-wrap">
            <label
              htmlFor={EVENT_CHECKOUT}
              className="btn btn-sm btn-success"
              disabled={
                UI.progress ||
                event.reason === '' ||
                (event.eventsForAction === '' && event.recurring === '1')
              }
            >
              <span className="icon checkmark-circle" /> Check Out
            </label>
          </div>
        );

      case EVENT_CHECKIN:
        const notFilledCounters =
          checkinForm.countersList &&
          checkinForm.countersList.filter(
            id =>
              !checkinForm.counters[id].newValue ||
              !checkinForm.counters[id].newValue.length ||
              isNaN(parseFloat(checkinForm.counters[id].newValue))
          );

        return (
          <div className="check-in-btns-wrap">
            <label
              htmlFor={`${EVENT_CHECKIN}-cancel`}
              className="btn btn-sm btn-default"
              disabled={UI.progress}
            >
              <span className="icon cancel-circled" /> Cancel
            </label>
            <label
              htmlFor={EVENT_CHECKIN}
              className="btn btn-sm btn-success"
              disabled={
                UI.progress ||
                this.getNotFilledCustomFieldsLength(checkinForm.customFields) ||
                (notFilledCounters && notFilledCounters.length)
              }
            >
              <span className="icon checkmark-circle" /> Check In
            </label>
          </div>
        );

      case EVENT_RESOURCE:
        if (
          !accessgroups.checkOutAircraft ||
          !availableToCheckoutOrCheckIn ||
          (parseInt(event.checkOutEnable, 10) !== 0 && parseInt(event.checkInEnable, 10) !== 1)
        )
          return null;

        if (parseInt(event.checkOutEnable, 10) === 0 && parseInt(event.checkInEnable, 10) === 0)
          return null;

        return (
          <div className="check-in-btns-wrap">
            <label
              htmlFor={EVENT_RESOURCE}
              className="btn btn-sm btn-success"
              disabled={UI.progress}
            >
              <span className="icon checkmark-circle" /> Check In
            </label>
          </div>
        );

      case DISCREPANCY_ADD:
        if (!accessgroups.discrepanciesAdd) return null;

        return (
          <label
            htmlFor={DISCREPANCY_ADD}
            className="btn btn-sm btn-success"
            disabled={
              UI.progress || !discrepancyForm.name.length || !discrepancyForm.description.length
            }
          >
            <span className="icon checkmark-circle" /> Add Discrepancy
          </label>
        );

      case DISCREPANCY_VIEW:
        if (!accessgroups.discrepanciesAdd) return null;

        return (
          <label
            htmlFor={DISCREPANCY_VIEW}
            className="btn btn-sm btn-danger"
            disabled={UI.progress}
          >
            <span className="icon trash" /> Delete Discrepancy
          </label>
        );

      case INSTRUCTORS_SCHEDULE:
        return (
          <span className="close-schedule">
            <label className="btn btn-default" htmlFor={INSTRUCTORS_SCHEDULE}>
              <span className="icon cancel-circled" /> Close
            </label>
          </span>
        );

      case EDIT_PASSENGER:
      case ADD_PASSENGER:
        return (
          <div className="display-flex">
            <label
              htmlFor={`${URL.eventParams.action}-cancel`}
              className="btn btn-default margin-l-r-5"
              disabled={UI.progress}
            >
              <span className="icon cancel-circled" /> Close
            </label>
            <label
              htmlFor={URL.eventParams.action}
              className="btn btn-success"
              disabled={UI.progress}
            >
              Save
            </label>
          </div>
        );

      case WEIGHT_AND_BALANCE:
        return (
          <div className="event-footer-btn-groups">
            <div className="event-footer-btn-group">
              <label
                htmlFor={`${URL.eventParams.action}-cancel`}
                className="btn btn-default margin-l-r-5 event-footer-btn"
                disabled={UI.progress}
              >
                <span className="icon cancel-circled" /> Close
              </label>
            </div>
            <div className="event-footer-btn-group">
              <div className="tooltip-container">
                {balanceStatus ?
                  (<span className="tooltip">Available only when W&B is within limits</span>) : null}
                <label
                  htmlFor={LOAD_MANIFEST}
                  className={`btn btn-default event-footer-btn`}
                  disabled={UI.progress || (balanceStatus && forceEnableManifest)}
                >
                  <span className="icon newspaper" /> Print Load Manifest
                </label>
              </div>
              <label
                htmlFor={URL.eventParams.action}
                className="btn btn-success event-footer-btn"
                disabled={UI.progress}
              >
                Save
              </label>
            </div>
          </div>
        );
    }
  }
}

const mapStateToProps: MapStateToProps<ReduxState, OwnProps, MappedState> = state => ({
  UI: state.UI,
  URL: state.URL,
  user: state.user,
  eventForm: state.eventForm,
  discrepancyForm: state.discrepancyForm,
  checkinForm: state.checkinForm,
  event: state.event,
  accessgroups: state.accessgroups,
  eventModalData: state.eventModalData,
  organization: state.organization,
  resources: state.resources,
  organizationsAccess: state.organizationsAccess,
  customFields: state.customFields,
  isNoChangesInEvents: selectIsNoChangesInEvents(state),
  weightAndBalance: state.weightAndBalance
});

const mapDispatchToProps = dispatch => ({
  dispatch,
  ...bindActionCreators(
    {
      getWeightAndBalance,
      setModal
    },
    dispatch
  )
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(EventModalFooter);
